/*
 * hover-zoom.js (v3 - inner / in-frame magnifier)
 * The zoomed view renders INSIDE the main image box itself, so it can
 * never overlap the thumbnails or the surrounding layout. Desktop +
 * fine-pointer only. Touch/coarse pointers keep the theme's existing
 * click-to-open zoom dialog untouched.
 *
 * Coexists with zoom-dialog.js:
 *  - reuses the data_max_resolution (3840px) URL the theme already sets
 *  - caps magnification at the image's true resolution (no pixel mush)
 *  - only suppresses the dialog-open click while a real mouse is in use
 *  - true resolution is read from the overlay image's load and cached
 *    per-URL; the overlay stays hidden until the high-res has decoded,
 *    so the first hover never flashes an empty frame.
 */
(function () {
  'use strict';

  var MIN_WIDTH = 750;        // desktop breakpoint, matches theme
  var TARGET_ZOOM = 2.5;      // medium magnification

  function isDesktopPointer() {
    return (
      window.matchMedia('(min-width: ' + MIN_WIDTH + 'px)').matches &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches
    );
  }

  var activeContainer = null;
  var overlay = null;
  var currentUrl = '';
  var ready = false;
  var dimCache = {};          // url -> { w, h }
  var loader = null;          // offscreen Image used to decode + measure

  function buildOverlay() {
    if (overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'hover-zoom__overlay';
    overlay.setAttribute('aria-hidden', 'true');
  }

  function teardown() {
    if (overlay) {
      overlay.classList.remove('is-ready');
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }
    if (activeContainer) activeContainer.classList.remove('hover-zoom--active');
    activeContainer = null;
    ready = false;
  }

  function mainImageOf(container) {
    return container.querySelector('img.product-media__image');
  }

  function highResUrlOf(img) {
    return (
      img.getAttribute('data_max_resolution') ||
      img.getAttribute('data-max-resolution') ||
      img.currentSrc ||
      img.src
    );
  }

  function reveal(container, url) {
    if (!activeContainer || url !== currentUrl) return;
    ready = true;
    overlay.style.backgroundImage = 'url("' + url + '")';
    overlay.classList.add('is-ready');
  }

  function onEnter(e) {
    if (!isDesktopPointer()) return;
    var container = e.target.closest
      ? e.target.closest('.product-media-container--zoomable.product-media-container--image')
      : null;
    if (!container || container.closest('.dialog-zoomed-gallery')) return;
    var img = mainImageOf(container);
    if (!img) return;

    buildOverlay();
    activeContainer = container;
    ready = false;

    // Ensure the container is a positioning context so the overlay clips to it.
    var cs = window.getComputedStyle(container);
    if (cs.position === 'static') container.style.position = 'relative';
    container.appendChild(overlay);
    container.classList.add('hover-zoom--active');

    var url = highResUrlOf(img);
    currentUrl = url;

    if (dimCache[url]) {
      reveal(container, url);
    } else {
      // Decode + measure once, then reveal (kept hidden until then).
      loader = new Image();
      loader.onload = (function (u, c) {
        return function () {
          dimCache[u] = { w: loader.naturalWidth || 0, h: loader.naturalHeight || 0 };
          reveal(c, u);
        };
      })(url, container);
      loader.src = url;
    }
  }

  function onMove(e) {
    if (!activeContainer || !ready) return;
    var container = activeContainer;
    var img = mainImageOf(container);
    if (!img) return teardown();

    var rect = img.getBoundingClientRect();
    var x = e.clientX - rect.left;
    var y = e.clientY - rect.top;

    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return teardown();

    // Cap zoom at the source's true resolution so we never upscale past 1:1.
    var dim = dimCache[currentUrl];
    var maxZoomW = dim && dim.w ? dim.w / rect.width : TARGET_ZOOM;
    var maxZoomH = dim && dim.h ? dim.h / rect.height : TARGET_ZOOM;
    var zoom = Math.min(TARGET_ZOOM, maxZoomW, maxZoomH);
    if (!isFinite(zoom) || zoom < 1) zoom = 1;

    // Match overlay box to the displayed image box (handles letterboxing).
    overlay.style.left = (img.offsetLeft || 0) + 'px';
    overlay.style.top = (img.offsetTop || 0) + 'px';
    overlay.style.width = rect.width + 'px';
    overlay.style.height = rect.height + 'px';

    // Background sized to zoom * displayed; positioned by cursor percentage,
    // which self-clamps at the edges (0%..100%).
    overlay.style.backgroundSize = (rect.width * zoom) + 'px ' + (rect.height * zoom) + 'px';
    var px = (x / rect.width) * 100;
    var py = (y / rect.height) * 100;
    overlay.style.backgroundPosition = px + '% ' + py + '%';
  }

  function onLeave(e) {
    if (!activeContainer) return;
    var to = e.relatedTarget;
    if (to && activeContainer.contains(to)) return;
    teardown();
  }

  function onClickCapture(e) {
    if (!isDesktopPointer()) return;
    if (e.pointerType === 'touch' || e.pointerType === 'pen') return;
    var container = e.target.closest
      ? e.target.closest('.product-media-container--zoomable.product-media-container--image')
      : null;
    if (!container || container.closest('.dialog-zoomed-gallery')) return;
    if (e.target.closest && e.target.closest('.product-media-container__zoom-button')) return;
    e.preventDefault();
    e.stopPropagation();
  }

  document.addEventListener('pointerover', onEnter, true);
  document.addEventListener('pointermove', onMove, true);
  document.addEventListener('pointerout', onLeave, true);
  document.addEventListener('click', onClickCapture, true);
  window.addEventListener('scroll', function () { if (activeContainer) teardown(); }, { passive: true });
  window.addEventListener('resize', function () { if (activeContainer) teardown(); });
})();
